// Lazy loader for the shielded client. The real module (./pool) statically
// pulls in circomlibjs (Poseidon), ethers, and fixed-merkle-tree, which add more
// than three megabytes to the bundle. Routing the dapp and the Info panel
// through this wrapper keeps that crypto out of the eager dapp chunk, so people
// who only trade, borrow, or read never download it. The pool module loads on
// first use of the Private flow.
//
// The lightweight reads (fetchPoolState, fetchRelayerInfo, fetchLoansFor) and the
// dependency-free emptyTransactArgs re-export straight from ./api and never
// trigger the pool download; this keeps the Info tab, the on-mount relayer poll,
// and listing or partly repaying a loan light. The remaining exports keep their
// real signatures, except the synchronous helpers (balanceOf, toTransactArgs,
// ownerCommitmentFor) become async, because a dynamic import can only resolve
// asynchronously. Each is always called right after an async call from this
// module, so the pool is already loaded by the time they run.
export { fetchPoolState, fetchRelayerInfo, fetchLoansFor, fetchAllLoans, emptyTransactArgs } from "./api";
export type { RelayerInfo, ShieldedPoolId } from "./api";
export type { Keypair, ProofProgress } from "./pool";
// clearProvingCache lives in ./assets, which is dependency-free, so re-exporting
// it here keeps the proof-data self-heal available to the dapp without pulling
// the heavy pool chunk.
export { clearProvingCache, prewarmProvingAssets } from "./assets";

type Pool = typeof import("./pool");

let cached: Promise<Pool> | null = null;
function load(): Promise<Pool> {
  return (cached ??= import("./pool"));
}

export const deriveKeypair: Pool["deriveKeypair"] = (...args) =>
  load().then((m) => m.deriveKeypair(...args));
export const scanOwnedNotes: Pool["scanOwnedNotes"] = (...args) =>
  load().then((m) => m.scanOwnedNotes(...args));
export const buildShield: Pool["buildShield"] = (...args) =>
  load().then((m) => m.buildShield(...args));
export const buildTransfer: Pool["buildTransfer"] = (...args) =>
  load().then((m) => m.buildTransfer(...args));
export const buildWithdraw: Pool["buildWithdraw"] = (...args) =>
  load().then((m) => m.buildWithdraw(...args));
export const buildCollateralWithdraw: Pool["buildCollateralWithdraw"] = (...args) =>
  load().then((m) => m.buildCollateralWithdraw(...args));
export const buildReshieldDeposit: Pool["buildReshieldDeposit"] = (...args) =>
  load().then((m) => m.buildReshieldDeposit(...args));
export const submitViaRelayer: Pool["submitViaRelayer"] = (...args) =>
  load().then((m) => m.submitViaRelayer(...args));

// Synchronous in ./pool; async here (see header). Callers add one await.
export const balanceOf = (
  ...args: Parameters<Pool["balanceOf"]>
): Promise<ReturnType<Pool["balanceOf"]>> =>
  load().then((m) => m.balanceOf(...args));
export const toTransactArgs = (
  ...args: Parameters<Pool["toTransactArgs"]>
): Promise<ReturnType<Pool["toTransactArgs"]>> =>
  load().then((m) => m.toTransactArgs(...args));
export const ownerCommitmentFor = (
  ...args: Parameters<Pool["ownerCommitmentFor"]>
): Promise<ReturnType<Pool["ownerCommitmentFor"]>> =>
  load().then((m) => m.ownerCommitmentFor(...args));
